import { useSelector } from "react-redux";
import { DataMainTemplate } from "@nocios/presentation-kit";
import Config from "src/Constants/Config";
import setCurrentPage from "src/Utils/setCurrentPage";

// Constants
const PAGE_ID = 7;
// const NEXT_PAGE_ID = PAGE_ID + 1;
const BACK_PAGE_ID = PAGE_ID - 1;
// const LAST_PAGE = true;
const BG_ID = 7;
const BG_TYPE = "jpg";

const USE_CASE = "USE CASE 4";
const LEFT_TITLE = "Automated Service Assurance\nvia Closed-Loop Remediation";
const LEFT_LIST_ITEM_1 = "AI-driven root cause analysis and problem isolation\n\n";

const LEFT_LIST_ITEM_2 = "Remediation Co-pilot through recommended\nactions\n\n";
const LEFT_LIST_ITEM_3 = "Automated workflows powered by BMC Helix\nIntelligent Automation\n\n";

// Video player component
const VideoPlayer = () => {
  return (
    <div
      style={{
        position: "relative",
        top: "2.5%",
        left: "6.5%",
        width: "92%", // Adjust as needed
        height: "auto",
        aspectRatio: "auto", // Ensures the container matches the video's aspect ratio
        overflow: "visible",
        zIndex: 9999,
      }}
    >
      {/* Video Player */}
      <video
        src={`${Config.BASE_URL}/images/videos/video_4.mp4`}
        autoPlay
        muted
        controls
        style={{
          position: "absolute",
          zIndex: 10,
          left: "var(--width-42)",
          top: "var(--height-36)",
          width: "var(--width-988)", // Matches the container's width
          height: "auto",
          display: "block",
        }}
      />
      <div style={{ position: "absolute", background: "#fff", width: "var(--width-900)", height: "var(--height-520)" }}></div>
      {/* Frame Overlay */}
      <img
        src={`${Config.BASE_URL}/images/videos/frame_1.png`}
        alt="Video Frame"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "var(--width-1035)", // Matches the container's width
          height: "auto", // Matches the container's height
          objectFit: "fill", // Ensures the frame matches the container dimensions exactly
          pointerEvents: "none", // Ensures it doesn’t block interactions with the video
        }}
      />
    </div>
  );
};

interface SliderWrapperProps {
  children: React.ReactNode;
}
const SliderWrapper = ({ children }: SliderWrapperProps) => (
  <div
    style={{
      position: "relative",
      overflowX: "hidden",
      width: "100%", // Ensures the wrapper fills the screen
    }}
  >
    {children}
  </div>
);

// Main data function that returns the template data
const DATA = (): DataMainTemplate => {
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  const baseImageBG = `${Config.BASE_URL}/images/BG/`;

  // Define keyframes for animations
  const keyframes = `
    @keyframes gradientMove {
      0% {
        background-position: 100% 50%;
      }
      100% {
        background-position: 0% 50%;
      }
    }
    @keyframes pulse {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.03);
      }
      100% {
        transform: scale(1);
      }
    }
    @keyframes shadowPulse {
      0% {
        box-shadow: 0 0 50px rgba(255,255,255, 0.3)
      }
      50% {
        box-shadow: 0 0 55px rgba(255,255,255,0.4);
      }
      100% {
        box-shadow: 0 0 50px rgba(255,255,255, 0.3);
      }
    }
  `;

  const leftBeforeTitle = (
    <p
      style={{
        marginBottom: "var(--width-8)",
        color: "#fff",
        fontFamily: "Tungsten",
        fontSize: "var(--width-40)",
        fontStyle: "normal",
        fontWeight: "500",
        lineHeight: "110%",
        textTransform: "uppercase",
        transform: "translateY(-210%)",
      }}
    >
      {USE_CASE}
    </p>
  );

  const leftAfterDescription = (
    <div style={{ transform: "translateY(-20%)", padding: "var(--width-10) 0" }}>
      <style>{keyframes}</style>
      <img
        src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/left_separator.png`}
        alt="separator"
        style={{
          width: "var(--width-565)",
          height: "auto",
        }}
      />
      <div
        style={{
          color: "#fff",
          fontFamily: "Myriad Pro",
          fontSize: "var(--width-24)",
          fontStyle: "normal",
          fontWeight: "380",
          lineHeight: "150%",
          whiteSpace: "pre-line",
        }}
      >
        <ul
          style={{
            marginTop: "var(--width-30)",
            marginBottom: "var(--width-10)",
          }}
        >
          <li style={{ fontSize: "var(--width-23)" }}>{LEFT_LIST_ITEM_1}</li>
          <li>{LEFT_LIST_ITEM_2}</li>
          <li>{LEFT_LIST_ITEM_3}</li>
        </ul>
      </div>
    </div>
  );

  const backButton =
    BACK_PAGE_ID > 0
      ? {
          label: "Back",
          type: "back" as const,
          startIcon: { icon: "arrow-right", color: "#fff" },
          onClick: () => setCurrentPage(BACK_PAGE_ID),
          style: {
            borderRadius: "45px",
            padding: "var(--width-5) var(--width-25)",
            gap: "var(--width-16)",
          },
        }
      : undefined;

  const nextButton = {
    label: "Finish",
    type: "next" as const,
    onClick: () => setCurrentPage(1),
    style: {
      borderRadius: "45px",
      padding: "5px 25px",
      gap: "var(--width-16)",
    },
  };

  return {
    bgVideo: {
      poster: `${baseImageBG}BG_${BG_ID}.${BG_TYPE}`,
      video: `${Config.BASE_URL}/videos/bg_${PAGE_ID}.mp4`,
    },
    gradient:
      "linear-gradient(270deg, rgba(21, 50, 121, 0.00) 11.77%, #153279 87.81%), linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) 100%)",

    title: LEFT_TITLE,
    styleTitle: {
      color: "#fff",
      fontFamily: "Tungsten",
      fontSize: "var(--width-64)",
      fontStyle: "normal",
      fontWeight: "500",
      lineHeight: "110%",
      textTransform: "uppercase",
      transform: "translateY(-65%)",
    },
    leftBeforeTitle: leftBeforeTitle,
    leftAfterDescription: leftAfterDescription,
    // Combine the VideoPlayer and ImageSlider in the right side component
    rightSideComponent: <SliderWrapper>{currentPage === PAGE_ID && <VideoPlayer />}</SliderWrapper>,
    next: nextButton,
    back: backButton,
  };
};

export default DATA;
